import React, { useEffect, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useLazyQuery, useQuery, useMutation } from '@apollo/client';
import rison from 'rison';

import {
  Root,
  ClassesSection,
  ClassesGrid,
  ClassCard,
  ClassImage,
  ClassContent,
  Brand,
  ClassName,
  Meta,
  Location,
  Points,
  FeaturedCardWrapper,
} from './EventsExperiences.style';

import { ProductsItem } from '../../../components/ProductsItem/ProductsItem';
import { Breadcrumbs } from '../../../components/Breadcrumbs/Breadcrumbs';
import { PageTitle } from '../../../components/PageTitle/PageTitle';
import { FiltersDesktop } from '../../../components/FiltersDesktop/FiltersDesktop';
import { FiltersMobile } from '../../../components/FiltersMobile/FiltersMobile';
import { SortByPanel } from '../../../components/SortByPanel/SortByPanel';
import { Pagination } from '../../../components/Pagination/Pagination';
import { EducationBlock } from '../../../components/EducationBlock/EducationBlock';
import { categoriesConfig, sortConfig, GET_EVENTS_TAGS, eventsTagPath, GET_EDUCATION_REWARDS } from './config';
import { apiDomain, apiSiteId } from '../../../config';
import eventsExperiencesImageSrc from '../../../assets/images/education/education_type_6.png';
import { UserContext } from '../../../context/UserContext';
import {
  GET_WISHLIST,
  ADD_TO_WISHLIST,
  REMOVE_FROM_WISHLIST,
} from '../../RewardsView/rewardsViewConfig';
import { toast, ToastNotification } from '../../../ui/ToastNotification/ToastNotification';

export const EventsExperiences = () => {
  const intl = useIntl();
  const locale = intl.locale;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(UserContext);

  const [getEducationRewards, { loading, data }] = useLazyQuery(GET_EDUCATION_REWARDS);

  const { data: wishlistData } = useQuery(GET_WISHLIST, {
    skip: user?._id == null,
    variables: { locale },
    fetchPolicy: 'cache-first',
  });

  const [addToWishlist] = useMutation(ADD_TO_WISHLIST, {
    refetchQueries: ['GetWishlist', 'GetHeaderCartData'],
  });

  const [removeFromWishlist] = useMutation(REMOVE_FROM_WISHLIST, {
    refetchQueries: ['GetWishlist', 'GetHeaderCartData'],
  });

  const handleAddToFavourites = async ({ item, inFavorites }) => {
    if (user?._id == null) {
      return navigate(`/${intl.locale}/auth?redirect=${encodeURIComponent(location.pathname)}`);
    }
    toast.dismiss();
    if (inFavorites) {
      toast.warn(
        <ToastNotification>
          <FormattedMessage
            defaultMessage="{productName} removed from wishlist."
            values={{ productName: item.name }}
          />
        </ToastNotification>,
      );
      await removeFromWishlist({ variables: { productId: item.id, locale } });
    } else {
      toast.success(
        <ToastNotification>
          <FormattedMessage
            defaultMessage="{productName} added to wishlist."
            values={{ productName: item.name }}
          />
        </ToastNotification>,
      );
      await addToWishlist({ variables: { productId: item.id, locale } });
    }
  };

  // Fetch education rewards on mount and when search params change
  useEffect(() => {
    let filters = searchParams.has('filters')
      ? rison.decode(searchParams.get('filters'))
      : {};

    filters.type = 'education-reward';
    filters.e = true;

    filters = JSON.stringify(filters);

    let sort = searchParams.get('sort');
    sort = sortConfig.find((s) => s.value === sort);
    sort = sort == null ? null : { key: sort.key, direction: sort.dir };

    const page = searchParams.has('page') ? searchParams.get('page') : 1;
    const limit = 200;

    getEducationRewards({
      variables: {
        sort,
        page,
        filters,
        limit,
        locale,
      },
      fetchPolicy: 'network-only',
    });
  }, [searchParams, locale, getEducationRewards]);

  const educationRewards = (data?.content?.findClubH?.docs || []).filter(
    (reward) => reward.data?.category === 'events-experiences'
  );
  
  const classes = educationRewards.map((reward) => {
    const educationData = reward.data || {};
    
    // Format date/time
    let datetime = '';
    if (educationData.dateTime) {
      const dt = new Date(educationData.dateTime);
      const dateStr = dt.toLocaleDateString(locale === 'fr-CA' ? 'fr-CA' : 'en-CA', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
      const timeStr = dt.toLocaleTimeString(locale === 'fr-CA' ? 'fr-CA' : 'en-CA', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
      datetime = `${dateStr} | ${timeStr}`;
    }

    // Format location - Note: Using "Location" instead of distributor name for Events & Experiences
    const locationParts = [
      educationData.locationName || 'Location',
      educationData.city,
      educationData.province,
    ].filter(Boolean);
    const location = locationParts.join(', ');

    // Format points (current and full price)
    const priceAmount = reward.ecommerce?.price?.amount || null;
    const fullPriceAmount = reward.ecommerce?.fullPrice?.amount || null;

    const points = priceAmount != null
      ? `${priceAmount.toLocaleString()} PTS`
      : '';

    const oldPoints = fullPriceAmount != null
      ? `${fullPriceAmount.toLocaleString()} PTS`
      : null;

    // Build full asset URL (same pattern as RewardsView)
    const mediaId = reward.gallery?.[0]?.media?._id;
    const imageSrc = mediaId
      ? `https://${apiDomain}/${apiSiteId}/asset/${mediaId}?type=fill&width=604&height=752`
      : null;

    return {
      id: reward._id,
      slug: reward.sl,
      brand: reward.brand?.n || '',
      name: reward.n || '',
      datetime,
      location,
      points,
      oldPoints,
      priceAmount,
      fullPriceAmount,
      imageSrc,
    };
  });

  useEffect(() => {
    // Inject global styles for featured card product name
    const styleId = 'events-experiences-featured-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        /* Target ProductName component in featured card with highest specificity */
        .featured-card-wrapper div[class*="ProductName"],
        .featured-card-wrapper a[href*="/rewards/"]:not(:first-child),
        .featured-card-wrapper > div > div > a:nth-of-type(2),
        .featured-card-wrapper > div > div > div > a[href*="/rewards/"] {
          font-family: "Yellix", system-ui, sans-serif !important;
          font-weight: 400 !important;
          font-style: normal !important;
          font-size: 18px !important;
          line-height: 22px !important;
          letter-spacing: 0.01em !important;
        }
        
        /* Override responsive styles at all breakpoints */
        @media (max-width: 1023px) {
          .featured-card-wrapper div[class*="ProductName"],
          .featured-card-wrapper a[href*="/rewards/"]:not(:first-child),
          .featured-card-wrapper > div > div > a:nth-of-type(2) {
            font-size: 18px !important;
            line-height: 22px !important;
          }
        }
        
        @media (min-width: 1024px) {
          .featured-card-wrapper div[class*="ProductName"],
          .featured-card-wrapper a[href*="/rewards/"]:not(:first-child),
          .featured-card-wrapper > div > div > a:nth-of-type(2) {
            font-size: 18px !important;
            line-height: 22px !important;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <Root>
      <FiltersMobile
        sortOptions={sortConfig}
        // categoriesConfig={categoriesConfig({ intl })}
        tagsConfig={{
          gql: GET_EVENTS_TAGS,
          dataPath: eventsTagPath,
        }}
        showPoints={true}
        pointsConfig={{ min: null, max: null }}
        searchPlaceholder="Search events and experiences"
      />

      <Breadcrumbs
        breadcrumbs={[
          {
            to: "/",
            label: intl.formatMessage({ defaultMessage: "Home" }),
          },
          {
            to: "/education",
            label: intl.formatMessage({ defaultMessage: "Education" }),
          },
          {
            to: null,
            label: intl.formatMessage({ defaultMessage: "Events & Experiences" }),
          },
        ]}
      />
      
      <PageTitle 
        title={intl.formatMessage({ defaultMessage: "EVENTS & EXPERIENCES" })} 
      />

      <FiltersDesktop
        // categoriesConfig={categoriesConfig({ intl })}
        tagsConfig={{
          gql: GET_EVENTS_TAGS,
          dataPath: eventsTagPath,
        }}
        pointsConfig={{ min: null, max: null }}
        showPoints={true}
        searchPlaceholder="Search events and experiences"
      />

      <SortByPanel
        sortOptions={sortConfig}
      />

      <ClassesSection>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <FormattedMessage defaultMessage="Loading..." />
          </div>
        ) : classes.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <FormattedMessage defaultMessage="No events found" />
          </div>
        ) : (
          <ClassesGrid>
            {/* Row 1: First 3 regular cards */}
            {classes.slice(0, 3).map((item) => (
              <ClassCard key={item.id} as={Link} to={`/education/class/${item.slug}`}>
                <ClassImage $imageSrc={item.imageSrc} />
                <ClassContent>
                  <Brand>{item.brand}</Brand>
                  <ClassName>{item.name}</ClassName>
                  <Meta>{item.datetime}</Meta>
                  <Location>{item.location}</Location>
                  <Points>
                    {item.oldPoints && (
                      <span
                        style={{
                          textDecoration: 'line-through',
                          marginRight: 6,
                        }}
                      >
                        {item.oldPoints}
                      </span>
                    )}
                    {item.points}
                  </Points>
                </ClassContent>
              </ClassCard>
            ))}

            {/* Row 2: one regular card + wide featured card */}
            {classes[3] && (
              <ClassCard as={Link} to={`/education/class/${classes[3].slug}`}>
                <ClassImage $imageSrc={classes[3].imageSrc} />
                <ClassContent>
                  <Brand>{classes[3].brand}</Brand>
                  <ClassName>{classes[3].name}</ClassName>
                  <Meta>{classes[3].datetime}</Meta>
                  <Location>{classes[3].location}</Location>
                  <Points>
                    {classes[3].oldPoints && (
                      <span
                        style={{
                          textDecoration: 'line-through',
                          marginRight: 6,
                        }}
                      >
                        {classes[3].oldPoints}
                      </span>
                    )}
                    {classes[3].points}
                  </Points>
                </ClassContent>
              </ClassCard>
            )}

            {classes[4] && (() => {
              const featuredItem = classes[4];
              const inFavorites = (wishlistData?.me?.wishlist?.items || []).some(
                (itm) => itm?.product?._id === featuredItem.id,
              );
              return (
                <FeaturedCardWrapper className="featured-card-wrapper">
                  <ProductsItem
                    isPromo={true}
                    imageSrc={featuredItem.imageSrc || eventsExperiencesImageSrc}
                    brandName={featuredItem.brand}
                    brandUrl={`/brands/${featuredItem.brand?.toLowerCase().replace(/\s+/g, '-')}`}
                    productName={featuredItem.name}
                    productUrl={`/education/class/${featuredItem.slug}`}
                    pts={featuredItem.priceAmount || 0}
                    oldPts={featuredItem.fullPriceAmount || undefined}
                    inFavorites={inFavorites}
                    onAddToFavourites={() => handleAddToFavourites({ item: featuredItem, inFavorites })}
                    onAddToCart={() => {}}
                    isEducation={true}
                    educationDate={featuredItem.datetime?.split(' | ')[0] || ''}
                    educationTime={featuredItem.datetime?.split(' | ')[1] || ''}
                    distributor={featuredItem.location?.split(',')[0] || 'Location'}
                    city={featuredItem.location?.split(',')[1]?.trim() || ''}
                    province={featuredItem.location?.split(',')[2]?.trim() || ''}
                  />
                </FeaturedCardWrapper>
              );
            })()}

            {/* Row 3: cards 5–7 */}
            {classes.slice(5, 8).map((item) => (
              <ClassCard key={item.id} as={Link} to={`/education/class/${item.slug}`}>
                <ClassImage $imageSrc={item.imageSrc} />
                <ClassContent>
                  <Brand>{item.brand}</Brand>
                  <ClassName>{item.name}</ClassName>
                  <Meta>{item.datetime}</Meta>
                  <Location>{item.location}</Location>
                  <Points>{item.points}</Points>
                </ClassContent>
              </ClassCard>
            ))}

            {/* Row 4+: all remaining cards */}
            {classes.slice(8).map((item) => (
              <ClassCard key={item.id} as={Link} to={`/education/class/${item.slug}`}>
                <ClassImage $imageSrc={item.imageSrc} />
                <ClassContent>
                  <Brand>{item.brand}</Brand>
                  <ClassName>{item.name}</ClassName>
                  <Meta>{item.datetime}</Meta>
                  <Location>{item.location}</Location>
                  <Points>
                    {item.oldPoints && (
                      <span style={{ textDecoration: 'line-through', marginRight: 6 }}>
                        {item.oldPoints}
                      </span>
                    )}
                    {item.points}
                  </Points>
                </ClassContent>
              </ClassCard>
            ))}
          </ClassesGrid>
        )}
      </ClassesSection>

      <Pagination
        totalCount={classes.length}
        currentPage={1}
        pageSize={200}
        onPaginate={(e) => {
          if (e === 1) searchParams.delete('page');
          else searchParams.set('page', e);
          setSearchParams(searchParams);
        }}
      />

      <EducationBlock excludePage="events-experiences" />
    </Root>
  );
};
